import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";

import MealModeToggle from "../plan/MealModeToggle";
import PlanCard from "../plan/PlanCard";
import PlanDinDins from "../plan/PlanDinDins";
import WhoIsInBoxBlock, { type WhoRow } from "../plan/WhoIsInBoxBlock";
import {
    PlanOfferBanner,
    PlanReassurance,
    PlanInlineCta,
    CleanBowlPromise,
} from "../plan/PlanLadderExtras";
import { FrozenBand, WhatYouSayCard, BowlsLickedBand } from "../review/ReviewSections";
import { quizAsset } from "@/components/quiz/shared/quizAssets";

import { calculateDailyIntake } from "@/domains/nutrition/DailyIntake";
import { caloriesFromGrams } from "@/domains/nutrition/FoodEnergy";
import { PACK_SIZES, type PackSize } from "@/domains/packs/PackSizes";
import {
    getPackPricingConfig,
    SELLING_PLAN_LABELS,
    type SellingPlanKey,
    buildMealAutoConfigSig,
    type PlanDays,
    isPlanLocked,
    LOCKED_PLAN_LABEL,
} from "@/helpers/plansToPacks";
import { useQuizContext } from "@/state/QuizContext";
import {
    useExperimentValue,
    QUIZ_PRESENTATION_EXPERIMENT_KEY,
} from "@/experiments/useExperimentValue";
import { planDaysForArm, defaultPlanDaysForArm } from "@/experiments/planLadder";
import { useDogInclusion } from "@/state/useDogInclusion";
import { useResultsStep } from "@/state/useResultsStep";
import { isDogIncluded } from "@/helpers/dogInclusion";
import { writeQuizStepToURL } from "@/state/urlState";
import { useProducts } from "@/state/ProductsContext";
import { GTM } from "@/services/gtm";
import { QUIZ } from "@/theme/quizTokens";

type LocalMealMode = "FULL" | "MIXED";

type Props = {
    dogs: { id: string; form: any }[];
    activeDogId: string | null;
};

const PLAN_OPTIONS: { days: PlanDays; key: SellingPlanKey }[] = [
    { days: 7, key: "DAY_7" },
    { days: 14, key: "DAY_14" },
    { days: 28, key: "DAY_28" },
];

const BASE_DISCOUNT = 0.25;
const getDiscountMultiplier = (): number => 1 - BASE_DISCOUNT;

/** "Coco", "Coco & Spot", "Coco, Spot & Rex" — for the "Made just for …" line. */
function formatDogNames(dogs: { form: any }[]): string {
    const names = dogs.map((d) => d.form?.name?.trim()).filter(Boolean);
    if (!names.length) return "your dog";
    if (names.length === 1) return names[0];
    return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
}

type MealVariant = {
    id: string | number;
    title: string;
    grams?: number;
    price: string | number;
    sku?: string;
};

type MealProduct = {
    id: string | number;
    title: string;
    handle: string;
    image?: string[] | string;
    images?: string[];
    variants: MealVariant[];
};

type PackPriceMeta = {
    priceGBP: number;
    variantId: string;
    productHandle?: string;
    productTitle?: string;
};

type DogPlan = {
    dogId: string;
    days: PlanDays;
    sellingPlanKey: SellingPlanKey;

    dailyGrams: number;
    dailyCalories: number;
    gramsNeeded: number;
    packSize: PackSize;
    variantId: string;
    totalPacks: number;

    totalFullPrice: number;
    totalDiscountPrice: number;
};

export default function ResultsStepPlan({ dogs }: Props) {
    const { dispatch } = useQuizContext();
    const { setDogIncluded } = useDogInclusion();
    const { hiddenPriceProducts, hiddenPriceMap: hiddenPriceIdMap } = useProducts();

    // Issue #50: excluded dogs keep their answers and plan values (so switching
    // them back in is instant) but contribute nothing to any price on the page.
    const includedDogs = useMemo(
        () => dogs.filter((dog) => isDogIncluded(dog.form)),
        [dogs]
    );

    // Experiment lever (presentation only, default = current copy).
    const offerHeadline = useExperimentValue(
        QUIZ_PRESENTATION_EXPERIMENT_KEY,
        "offer_headline",
        "Today's offer: 25% off your first 2 boxes"
    );

    // Plan-ladder experiment (#125) concluded 2026-10-22: control (7/14/28,
    // 7-day pre-selected) won. Hard-wired to control so visitors still holding
    // an arm-"b" assignment fall back too; remove the two-plan branch once
    // the #125 cohort reads are done.
    const twoPlan = false;
    const { setResultsStep } = useResultsStep();

    const [selectedPlanDays, setSelectedPlanDays] = useState<PlanDays>(() =>
        defaultPlanDaysForArm(twoPlan)
    );
    // ✅ Hydrate mealMode from saved form instead of always starting with "FULL"
    const [mealMode, setMealMode] = useState<LocalMealMode>(() => {
        const first = dogs[0]?.form;
        const saved = first?.mealMode;
        return (saved === "MIXED" || saved === "FULL") ? saved : "FULL";
    });

    const hasHydratedPlanDaysRef = useRef(false);
    const hasPlanSelectionTrackedRef = useRef(false);
    const [loadTimeout, setLoadTimeout] = useState(false);

    useEffect(() => {
        if (hasHydratedPlanDaysRef.current) return;
        if (!dogs.length) return;

        const first = dogs[0].form;
        const days = Number(first?.selectedPlanDays);

        if (days === 7 || days === 14 || days === 28) {
            // A saved session on a now-locked plan falls back to the longest
            // available cadence instead of restoring the locked one; a saved
            // 7-day choice in the two_plan arm falls back to that arm's default.
            let restored: PlanDays = isPlanLocked(days) ? 14 : (days as PlanDays);
            if (!planDaysForArm(twoPlan).includes(restored)) {
                restored = defaultPlanDaysForArm(twoPlan);
            }
            setSelectedPlanDays(restored);
        }

        hasHydratedPlanDaysRef.current = true;
    }, [dogs, twoPlan]);

    const productsLoading = !hiddenPriceProducts.length;

    // ✅ Add timeout to prevent infinite loading spinner
    useEffect(() => {
        if (!productsLoading) return;

        const timer = setTimeout(() => {
            console.error("Hidden price products failed to load after 10s");
            setLoadTimeout(true);
        }, 10000);

        return () => clearTimeout(timer);
    }, [productsLoading]);

    // ✅ IMPORTANT FIX:
    // Build pack price meta PER planDays, because you have 3 different hidden products
    // and they contain different prices.
    const packPriceMetaByDays: Record<
        PlanDays,
        Partial<Record<PackSize, PackPriceMeta>>
    > = useMemo(() => {
        const out: Record<PlanDays, Partial<Record<PackSize, PackPriceMeta>>> = {
            7: {},
            14: {},
            28: {},
        };

        const products = hiddenPriceProducts as MealProduct[];
        const PLAN_DAYS: PlanDays[] = [7, 14, 28];

        // Build reverse map: productId → days
        const idToDays: Record<string, PlanDays> = {};
        const hasIdMap = hiddenPriceIdMap && Object.keys(hiddenPriceIdMap).length > 0;

        if (hasIdMap) {
            for (const [days, id] of Object.entries(hiddenPriceIdMap!)) {
                const daysNum = Number(days) as PlanDays;
                if (PLAN_DAYS.includes(daysNum)) {
                    idToDays[id] = daysNum;
                }
            }
        }

        // Fallback: infer days from product title/handle
        const inferDaysFromProduct = (product: MealProduct): PlanDays | null => {
            const title = String(product.title ?? "").toLowerCase();
            const handle = String(product.handle ?? "").toLowerCase();
            const combined = `${title} ${handle}`;

            for (const days of PLAN_DAYS) {
                if (combined.includes(`${days}-day`) || combined.includes(`${days} day`)) {
                    return days;
                }
            }

            // Legacy fallback: "hidden-plans-product" defaults to 7-day
            if (handle === "hidden-plans-product" || handle.includes("hidden-plan")) {
                return 7;
            }

            return null;
        };

        // Process each product using ID map first, fallback to string matching
        for (const product of products) {
            // Primary: Use ID map for lookup
            let days = idToDays[String(product.id)];

            // Fallback: Use string matching if ID map is empty or doesn't have this product
            if (!days) {
                days = inferDaysFromProduct(product) ?? undefined as unknown as PlanDays;
                if (days) {
                    console.log("[ResultsStepPlan] Using fallback string matching for product", product.id, product.title, "→", days, "days");
                }
            }

            if (!days) {
                console.warn("[ResultsStepPlan] No days mapping for product", product.id, product.title);
                continue;
            }

            for (const variant of product.variants || []) {
                const gramsFromTitle = parseInt(String(variant.title), 10);
                const grams = gramsFromTitle as PackSize;

                if (!PACK_SIZES.includes(grams)) continue;

                const priceGBP = Number(variant.price);
                if (!Number.isFinite(priceGBP)) continue;

                out[days][grams] = {
                    priceGBP,
                    variantId: String(variant.id),
                    productHandle: product.handle,
                    productTitle: product.title,
                };
            }
        }

        return out;
    }, [hiddenPriceProducts, hiddenPriceIdMap]);

    const dogIntakes = useMemo(() => {
        return dogs.reduce<Record<string, { gramsPerDay: number; caloriesPerDay: number }>>(
            (acc, dog) => {
                const f = dog.form || {};

                // ✅ Prefer persisted values from earlier steps
                const gramsFromForm = Number(f.dailyGrams);

                if (Number.isFinite(gramsFromForm) && gramsFromForm > 0) {
                    acc[dog.id] = {
                        gramsPerDay: gramsFromForm,
                        caloriesPerDay: caloriesFromGrams(gramsFromForm),
                    };
                    return acc;
                }

                // Fallback (only if dailyGrams missing)
                const { weightKg, years, bodyCondition, activityLevel } = f;
                if (weightKg == null || years == null || !bodyCondition || !activityLevel) {
                    acc[dog.id] = { gramsPerDay: 0, caloriesPerDay: 0 };
                    return acc;
                }

                try {
                    const intake = calculateDailyIntake({
                        weightKg: Number(weightKg),
                        ageYears: Number(years) || 0,
                        bodyCondition: (bodyCondition || "ideal").toLowerCase(),
                        activityLevel: (activityLevel || "medium").toLowerCase(),
                    });

                    acc[dog.id] = {
                        gramsPerDay: intake.gramsPerDay,
                        caloriesPerDay: caloriesFromGrams(intake.gramsPerDay),
                    };
                } catch {
                    acc[dog.id] = { gramsPerDay: 0, caloriesPerDay: 0 };
                }

                return acc;
            },
            {}
        );
    }, [dogs]);

    const isLoading =
        (dogs.length > 0 && Object.keys(dogIntakes).length === 0) ||
        productsLoading ||
        Object.keys(packPriceMetaByDays[7]).length === 0 ||
        Object.keys(packPriceMetaByDays[14]).length === 0 ||
        Object.keys(packPriceMetaByDays[28]).length === 0;

    const modeFactor = mealMode === "FULL" ? 1 : 0.6;

    const currentPlanKey: SellingPlanKey =
        PLAN_OPTIONS.find((p) => p.days === selectedPlanDays)?.key || "DAY_7";

    const dogPlans: Record<string, DogPlan> = useMemo(() => {
        const plans: Record<string, DogPlan> = {};
        const discountMultiplier = getDiscountMultiplier();

        dogs.forEach((dog) => {
            const intake = dogIntakes[dog.id];
            if (!intake || !intake.gramsPerDay) return;

            const rawDailyGrams = intake.gramsPerDay * modeFactor;

            const { dailyKey, packSize: rawPackSize, totalPacks, totalGrams } =
                getPackPricingConfig(rawDailyGrams, selectedPlanDays);

            const fallbackSize: PackSize = 400;
            const packSize: PackSize = PACK_SIZES.includes(rawPackSize as PackSize)
                ? (rawPackSize as PackSize)
                : fallbackSize;

            // ✅ Use the hidden price product for THIS planDays
            const meta = packPriceMetaByDays[selectedPlanDays]?.[packSize];
            const pricePerPack = meta?.priceGBP ?? 0;

            const totalFullPrice = totalPacks * pricePerPack;
            const totalDiscountPrice = totalFullPrice * discountMultiplier;

            plans[dog.id] = {
                dogId: dog.id,
                days: selectedPlanDays,
                sellingPlanKey: currentPlanKey,
                dailyGrams: dailyKey,
                dailyCalories: intake.caloriesPerDay,
                gramsNeeded: totalGrams,
                packSize,
                variantId: meta?.variantId ?? "",
                totalPacks,
                totalFullPrice,
                totalDiscountPrice,
            };
        });

        return plans;
    }, [dogs, dogIntakes, modeFactor, selectedPlanDays, currentPlanKey, packPriceMetaByDays]);

    // ✅ Track last dispatched values to prevent unnecessary re-renders
    const lastDispatchedRef = useRef<Record<string, string>>({});

    useEffect(() => {
        Object.values(dogPlans).forEach((plan) => {
            if (!plan.packSize) return;

            const dog = dogs.find((d) => d.id === plan.dogId);
            const existingForm = dog?.form || {};

            const sig = buildMealAutoConfigSig({
                planDays: plan.days,
                dailyKey: plan.dailyGrams,
                packSize: plan.packSize,
                totalPacks: plan.totalPacks,
            });

            // ✅ Only dispatch if signature actually changed
            if (lastDispatchedRef.current[plan.dogId] === sig) return;

            const needsUpdate =
                existingForm.selectedPackSize !== plan.packSize ||
                existingForm.selectedPlanDays !== plan.days ||
                existingForm.mealMode !== mealMode ||
                existingForm.dailyGramsEffective !== plan.dailyGrams;

            if (!needsUpdate) return;

            lastDispatchedRef.current[plan.dogId] = sig;

            dispatch({
                type: "UPDATE_DOG_FORM",
                dogId: plan.dogId,
                payload: {
                    selectedPlanDays: plan.days,
                    selectedPackSize: plan.packSize,
                    mealMode,
                    dailyKey: plan.dailyGrams,
                    planTotalPacks: plan.totalPacks,
                    mealAutoConfigSig: sig,
                },
            });
        });
    }, [dogPlans, dogs, dispatch, mealMode]);

    // Hooks must run on every render, so these sit above the loading/error
    // early returns (previously below them: a loading -> loaded transition
    // would throw "Rendered more hooks than during the previous render").
    const discountMultiplierForUI = getDiscountMultiplier();

    // ✅ Memoize plan calculations to prevent recalculation on every render
    const planPricing = useMemo(() => {
        const offered = planDaysForArm(twoPlan);
        return PLAN_OPTIONS.filter(({ days }) => offered.includes(days)).map(({ days, key }) => {
            let totalFull = 0;

            includedDogs.forEach((dog) => {
                const intake = dogIntakes[dog.id];
                if (!intake || !intake.gramsPerDay) return;

                const rawDailyGrams = intake.gramsPerDay * modeFactor;
                const { packSize: rawPackSize, totalPacks } = getPackPricingConfig(
                    rawDailyGrams,
                    days
                );

                const fallbackSize: PackSize = 400;
                const packSize: PackSize = PACK_SIZES.includes(rawPackSize as PackSize)
                    ? (rawPackSize as PackSize)
                    : fallbackSize;

                const meta = packPriceMetaByDays[days]?.[packSize];
                const pricePerPack = meta?.priceGBP ?? 0;

                const dogFull = totalPacks * pricePerPack;
                totalFull += dogFull;
            });

            const totalDiscount = totalFull * discountMultiplierForUI;
            const discountedPricePerDay = days > 0 ? totalDiscount / days : 0;
            const fullPricePerDay = days > 0 ? totalFull / days : 0;

            return {
                days,
                key,
                totalFull,
                totalDiscount,
                discountedPricePerDay,
                fullPricePerDay,
            };
        });
    }, [includedDogs, dogIntakes, modeFactor, packPriceMetaByDays, discountMultiplierForUI, twoPlan]);

    // ✅ useCallback for click handlers to prevent re-creating functions
    const handlePlanSelect = useCallback((days: PlanDays) => {
        if (isPlanLocked(days)) return;
        setSelectedPlanDays(days);

        // Find pricing for selected plan
        const planData = planPricing.find(p => p.days === days);
        if (planData) {
            GTM.trackPlanSelected({
                planDays: days,
                totalPrice: planData.totalDiscount,
                pricePerDay: planData.discountedPricePerDay,
                mealMode: mealMode,
                isInitialSelection: !hasPlanSelectionTrackedRef.current,
            });
            hasPlanSelectionTrackedRef.current = true;
        }
    }, [planPricing, mealMode]);

    const handleMealModeToggle = useCallback((mode: LocalMealMode) => {
        const previousMode = mealMode;
        setMealMode(mode);

        // Track meal mode change
        GTM.trackMealModeChanged({
            previousMode,
            newMode: mode,
            planDays: selectedPlanDays,
        });
    }, [mealMode, selectedPlanDays]);

    if (isLoading && !loadTimeout) {
        return (
            <Box sx={{ textAlign: "center", mt: 10 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2, color: "#4A3428" }}>
                    Loading plan options...
                </Typography>
            </Box>
        );
    }

    // ✅ Show error message if products fail to load
    if (loadTimeout) {
        return (
            <Box sx={{ textAlign: "center", mt: 10, px: 3 }}>
                <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#D14C3F", mb: 2 }}>
                    Unable to Load Plan Options
                </Typography>
                <Typography sx={{ fontSize: 16, color: "#4A3428", mb: 3 }}>
                    We're having trouble loading the subscription plans. Please refresh the page or contact support.
                </Typography>
                <Button
                    onClick={() => window.location.reload()}
                    sx={{
                        background: "#FF7874",
                        color: "#fff",
                        px: 4,
                        py: 1.5,
                        borderRadius: "999px",
                        fontWeight: 700,
                        "&:hover": { background: "#E96A67" }
                    }}
                >
                    Refresh Page
                </Button>
            </Box>
        );
    }


    // "+ Add another dog" (issue #50): the new dog answers its questions from
    // the start of the question flow; every other dog's answers are untouched.
    // ADD_DOG makes the new dog active and sets its step to "basic".
    // (Plain function, not useCallback — this section of the component sits
    // after the loading early-returns, where no new hooks may be added.)
    const handleAddDog = () => {
        dispatch({ type: "ADD_DOG" });
        dispatch({ type: "SET_GLOBAL_STEP", step: "basic" });
        writeQuizStepToURL("basic");
    };

    // Per-dog rows for the "Who's in this box" block. Excluded dogs still get
    // their numbers from dogPlans, so their struck-through price stays visible.
    const whoRows: WhoRow[] = dogs
        .map((dog) => {
            const plan = dogPlans[dog.id];
            if (!plan) return null;
            return {
                dogId: dog.id,
                name: dog.form?.name?.trim() || "Your dog",
                sex: dog.form?.sex,
                included: isDogIncluded(dog.form),
                dailyGrams: plan.dailyGrams,
                discount: plan.totalDiscountPrice,
                full: plan.totalFullPrice,
            };
        })
        .filter(Boolean) as WhoRow[];

    const selectedPlanPricing = planPricing.find((p) => p.days === selectedPlanDays);

    const copyDogNames = (includedDogs.length ? includedDogs : dogs)
        .map((d) => d.form?.name?.trim())
        .filter(Boolean) as string[];

    // two_plan inline CTA: same as the footer's "Next" (plan → meal selection).
    const handleInlineCta = () => {
        setResultsStep(3);
        window.scrollTo({ top: 0 });
    };

    return (
        <Box
            sx={{
                width: "100%",
                boxSizing: "border-box",
            }}
        >
            {/* Intro — CHOOSE YOUR PLAN */}
            <Box sx={{ textAlign: "center", maxWidth: 600, mx: "auto", px: { xs: "1px", md: 0 }, mt: { xs: 0, md: 1 } }}>
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: QUIZ.font.display,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        color: QUIZ.color.ink,
                        fontSize: { xs: "34px", md: "41px" },
                        lineHeight: 1.15,
                        mb: { xs: "10px", md: "12px" },
                    }}
                >
                    Choose your plan
                </Typography>
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.body,
                        fontSize: { xs: 16, md: 17 },
                        lineHeight: 1.35,
                        color: QUIZ.color.ink,
                        opacity: 0.9,
                        maxWidth: { xs: 300, md: "none" },
                        mx: "auto",
                    }}
                >
                    Made just for {formatDogNames(includedDogs.length ? includedDogs : dogs)}
                </Typography>
            </Box>

            {/* DIN DINS! opening-offer band */}
            <PlanDinDins />

            {/* Brown "Choose your plan" card — 601px inner column on desktop (Figma 1586:11874) */}
            <Box sx={{ minWidth: 0, width: "100%", maxWidth: { xs: "100%", sm: 520, md: 700 }, mx: "auto", mt: { xs: "0px", md: "8px" }, position: "relative", py: { xs: "34px", md: "44px" } }}>
                <Box
                    sx={{
                        bgcolor: QUIZ.color.brown,
                        borderRadius: "20px",
                        p: { xs: "20px 16px", md: "44px 48px" },
                        position: "relative",
                    }}
                >
                    {/* Kuillo heading + blue sparkle (Figma VECTOR ART) */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: { xs: "10px", md: "16px" }, mb: { xs: "16px", md: "24px" } }}>
                        <Box
                            component="img"
                            src={quizAsset("icon-sparkle")}
                            alt=""
                            sx={{ width: { xs: 34, md: 56 }, height: "auto", display: "block", flexShrink: 0 }}
                        />
                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.label,
                                fontSize: { xs: 22, md: 27 },
                                color: QUIZ.color.cream,
                                textAlign: "center",
                            }}
                        >
                            Choose your plan
                        </Typography>
                    </Box>

                    {/* Offer subhead */}
                    {twoPlan ? <PlanOfferBanner /> : <Typography
                        sx={{
                            fontFamily: QUIZ.font.body,
                            fontWeight: 700,
                            fontSize: { xs: 14, md: 17 },
                            color: QUIZ.color.blue,
                            textAlign: "center",
                            mt: { xs: "-8px", md: "-14px" },
                            mb: { xs: "16px", md: "22px" },
                        }}
                    >
                        {offerHeadline}
                    </Typography>}

                    {/* Toggle: 100% vs 60-40% */}
                    <MealModeToggle mealMode={mealMode} onToggle={handleMealModeToggle} wrapLabels={twoPlan} />

                    {/* Plan rows */}
                    {planPricing.map((pricing) => {
                        const { days, key, totalFull, totalDiscount, discountedPricePerDay, fullPricePerDay } = pricing;
                        const planLabel = twoPlan
                            ? SELLING_PLAN_LABELS[key].replace(" day", "-day")
                            : SELLING_PLAN_LABELS[key];
                        const selected = selectedPlanDays === days;

                        return (
                            <PlanCard
                                key={days}
                                days={days}
                                planLabel={planLabel}
                                selected={selected}
                                totalDiscount={totalDiscount}
                                totalFull={totalFull}
                                discountedPricePerDay={discountedPricePerDay}
                                fullPricePerDay={fullPricePerDay}
                                onSelect={() => handlePlanSelect(days)}
                                locked={isPlanLocked(days)}
                                lockedLabel={LOCKED_PLAN_LABEL}
                                twoPlan={twoPlan}
                            />
                        );
                    })}

                    {/* Per-dog include/exclude — multi-dog households only (issue #50) */}
                    {dogs.length > 1 && whoRows.length > 0 && (
                        <WhoIsInBoxBlock
                            rows={whoRows}
                            days={selectedPlanDays}
                            totalDiscount={selectedPlanPricing?.totalDiscount ?? 0}
                            onToggle={(dogId, included) => setDogIncluded(dogId, included)}
                            onAddDog={handleAddDog}
                        />
                    )}

                    {twoPlan && <PlanReassurance dogNames={copyDogNames} />}
                </Box>
            </Box>

            {twoPlan && (
                <>
                    <PlanInlineCta dogNames={copyDogNames} onClick={handleInlineCta} />
                    <CleanBowlPromise dogNames={copyDogNames} />
                </>
            )}

            {/* Marketing sections (full-bleed, shared with the review step) */}
            <FrozenBand />
            <Box sx={{ maxWidth: 1030, mx: "auto" }}>
                <WhatYouSayCard />
            </Box>
            <BowlsLickedBand />
        </Box>
    );
}